// CouncilOutput.jsx — expandable phase output using the EXISTING MarkdownMessage
// renderer (react-markdown, escape-by-default: no rehype-raw anywhere in the
// pipeline, so raw HTML renders inert). SPEC §16 explicitly requires reusing the
// shared sanitized renderer — no bespoke regex Markdown.

import { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import MarkdownMessage from '../MarkdownMessage.jsx';

/** Copy button shared by the expandable cards and the export action. */
export function CopyButton({ text, label = 'Copiar' }) {
  const [copied, setCopied] = useState(false);
  async function onCopy() {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard may be unavailable (permissions/insecure context); no-op.
    }
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={copied ? 'Copiado' : label}
      title={copied ? 'Copiado!' : label}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accentRing"
    >
      {copied ? <Check size={11} aria-hidden="true" /> : <Copy size={11} aria-hidden="true" />}
      {copied ? 'Copiado' : label}
    </button>
  );
}

/**
 * Expandable card for one agent's output (opinion / rebuttal / synthesis).
 * Collapsed shows a truncated preview + expand affordance; expanded renders the
 * full sanitized Markdown and exposes copy.
 */
export function ExpandableOutput({ title, subtitle, accentColor, content, defaultOpen = false, errorMessage, streaming }) {
  const [open, setOpen] = useState(defaultOpen);
  const text = errorMessage ? `⚠️ ${errorMessage}` : (content || '');
  const isEmpty = !errorMessage && !content;
  const preview = text.length > 220 ? `${text.slice(0, 220)}…` : text;

  return (
    <article
      className="relative flex flex-col overflow-hidden rounded-xl border bg-apolo-surface1"
      style={accentColor ? { borderColor: `${accentColor}55` } : undefined}
    >
      <header className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex flex-1 items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
        >
          {open ? <ChevronDown size={13} aria-hidden="true" className="text-apolo-textTertiary" />
            : <ChevronRight size={13} aria-hidden="true" className="text-apolo-textTertiary" />}
          <span className="min-w-0">
            <span className="block truncate text-xs font-semibold text-apolo-textPrimary">{title}</span>
            {subtitle && <span className="block truncate text-[10px] text-apolo-textTertiary">{subtitle}</span>}
          </span>
        </button>
        {!isEmpty && <CopyButton text={text} />}
      </header>
      <div className="border-t border-apolo-borderSubtle/60 px-3 py-2">
        {isEmpty ? (
          <p className="text-[11px] text-apolo-textTertiary">
            {streaming ? 'Recebendo…' : 'Sem conteúdo.'}
          </p>
        ) : open ? (
          errorMessage ? (
            <p role="alert" className="whitespace-pre-wrap text-xs leading-relaxed text-apolo-error">{text}</p>
          ) : (
            <MarkdownMessage content={content} />
          )
        ) : (
          <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-apolo-textSecondary">{preview}</p>
        )}
      </div>
    </article>
  );
}

/** Small labelled section header for the phase rail. */
export function SectionHeader({ index, label, color, active, done, count }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <span
        aria-hidden="true"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold"
        style={{
          backgroundColor: active ? color : done ? `${color}30` : 'var(--apolo-surface2)',
          color: active ? 'var(--apolo-bg)' : done ? color : 'var(--apolo-textTertiary)',
          boxShadow: active ? `0 0 18px ${color}55` : 'none',
        }}
      >
        {index}
      </span>
      <h2 className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: active || done ? color : undefined }}>
        {label}
      </h2>
      {typeof count === 'number' && count > 0 && (
        <span className="rounded-full bg-apolo-surface2 px-1.5 py-0.5 text-[9px] font-medium tabular-nums text-apolo-textSecondary">
          {count}
        </span>
      )}
      <div className="h-px flex-1 bg-gradient-to-r from-apolo-borderSubtle to-transparent" />
    </div>
  );
}
